import {Component, inject, input, OnChanges} from '@angular/core'
import {MatDialog} from '@angular/material/dialog'
import {MatExpansionPanel, MatExpansionPanelHeader} from '@angular/material/expansion'
import {MatTooltip} from '@angular/material/tooltip'
import {Kalpylator} from '@sparbanken-syd/kalpylator'
import {KalpItemComponent} from '../90-kalp-item/kalp-item.component'
import {CopyComponent} from '../98-copy/copy.component'
import {DetailDialogComponent} from '../99-detail-dialog/detail-dialog.component'

interface KalpDisplayItem {

  /**
   * Index to display these in order. Use sort.
   */
  index: number

  /**
   * The text to display
   */
  label: string

  /**
   * The actual value
   */
  value: number

  /**
   * A + or a - to be displayed _before_
   */
  plusMinus: string

  /**
   * If this should be treated as a summary or not
   */
  summary: boolean

  /**
   * Number of decimals on number value
   */
  decimals: string
}

@Component({
  selector: 'spb-kalp-display',
  templateUrl: './kalp-display.component.html',
  styleUrls: ['./kalp-display.component.scss'],
  imports: [MatExpansionPanel, MatExpansionPanelHeader, MatTooltip, KalpItemComponent, CopyComponent]
})
export class KalpDisplayComponent implements OnChanges {
  /**
   * The kalpylator with values inside.
   */
  public readonly kalpylator = input<Kalpylator>(new Kalpylator())

  public readonly title = input('Borgo')

  /**
   * This is so that we can get change detection on updated
   * kalps.
   */
  public readonly kalp = input(0)

  public items: KalpDisplayItem[] = []

  private readonly dialog = inject(MatDialog)

  public ngOnChanges(): void {
    this.items.length = 0
    let i = 0

    const kalpylator = this.kalpylator()
    this.items.push(
      {
        index: i++,
        label: 'Inkomster',
        plusMinus: '+',
        summary: false,
        value: kalpylator.monthlyNetIncome,
        decimals: '0'
      },
      {
        index: i++,
        label: 'Levnadsomkostnader',
        plusMinus: '-',
        summary: false,
        value: kalpylator.monthlyLivingExpense, decimals: '0'
      },
      {
        index: i++,
        label: 'Barn',
        plusMinus: '-',
        summary: false,
        // For presentation purposes make sure all values are presented as positive
        value: (kalpylator.monthlyChildrenIncome - kalpylator.monthlyChildrenCost) * -1,
        decimals: '0'
      },
      {
        index: i++, label: 'Bil', plusMinus: '-', summary: false,
        // For presentation purposes make sure all values are presented as positive
        value: kalpylator.monthlyCarCost,
        decimals: '0'
      },
      {
        index: i++,
        label: 'Boende',
        plusMinus: '-',
        summary: false,
        value: kalpylator.monthlyPropertyCost,
        decimals: '0'
      },
      {
        index: i++,
        label: 'Lån',
        plusMinus: '-',
        summary: false,
        value: kalpylator.monthlyLoanCost + kalpylator.monthlyLoanTaxReturn,
        decimals: '0'
      },
      {
        index: i++,
        label: 'Skattereduktion',
        plusMinus: '+',
        summary: false,
        value: kalpylator.monthlyLoanTaxReturn,
        decimals: '0'
      },
      {
        index: i,
        label: '',
        plusMinus: '',
        summary: true,
        value: kalpylator.kalp,
        decimals: '0'
      })
  }

  public copy = (): string => {
    let res = ''
    this.items
      .filter((item: KalpDisplayItem) => !item.summary)
      .forEach((item: KalpDisplayItem) => {

        let val = Number(item.value ?? '')

        if (item.plusMinus === '-') {
          val = val * -1
        }
        const p = val
          .toFixed(+item.decimals)
          .replace('.', ',')
        res += `${item.label}\t${p}` + '\n'
      })
    return res
  }

  public open(): void {
    this.dialog.open(DetailDialogComponent, {
      maxWidth: 800,
      data: {
        kalpylator: this.kalpylator()
      }
    })
  }
}
